import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  doc,
  updateDoc, 
  deleteDoc, 
  getDoc 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaTrash, FaPencilAlt, FaCheck, FaTimes, FaSpinner, FaEdit } from 'react-icons/fa';
import { updateOrderStatus, subscribeToAdminOrders } from '../../firebase/services';
import styles from './Orders.module.css';

// Order Detail Modal Component
const OrderDetailModal = ({ order, isOpen, onClose, onSave, orderStatuses }) => {
  const [editedOrder, setEditedOrder] = useState({});
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (order) {
      setEditedOrder({ ...order });
    }
  }, [order]);
  
  if (!isOpen || !order) return null;
  
  const handleChange = (field, value) => {
    // Handle nested fields like customerInfo.name
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditedOrder(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent] || {}),
          [child]: value
        }
      }));
    } else {
      setEditedOrder(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editedOrder);
      onClose();
    } catch (error) {
      console.error('Error saving order:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Helper to format prices
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Order #{order.orderNumber || order.id.substr(0, 8)}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.modalSection}>
            <h3>Order Status</h3>
            <select
              value={editedOrder.status || 'pending'}
              onChange={(e) => handleChange('status', e.target.value)}
              className={`${styles.statusSelect} ${styles[editedOrder.status || 'pending']}`}
            >
              {orderStatuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          <div className={styles.modalSection}>
            <h3>Customer Information</h3>
            <div className={styles.formGroup}>
              <label>Name</label>
              <input
                type="text"
                value={editedOrder.customerInfo?.name || ''}
                onChange={(e) => handleChange('customerInfo.name', e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Phone</label>
              <input
                type="text"
                value={editedOrder.customerInfo?.phone || ''}
                onChange={(e) => handleChange('customerInfo.phone', e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Email</label>
              <input
                type="email"
                value={editedOrder.customerInfo?.email || ''}
                onChange={(e) => handleChange('customerInfo.email', e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Address</label>
              <textarea
                value={editedOrder.customerInfo?.address || ''}
                onChange={(e) => handleChange('customerInfo.address', e.target.value)}
                rows={3}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Notes</label>
              <textarea
                value={editedOrder.customerInfo?.notes || ''}
                onChange={(e) => handleChange('customerInfo.notes', e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <div className={styles.modalSection}>
            <h3>Order Items</h3>
            {(editedOrder.items || []).map((item, index) => (
              <div key={index} className={styles.orderItem}>
                <div className={styles.itemDetails}>
                  <h4>{item.name}</h4>
                  <div className={styles.itemInfo}>
                    <p><strong>Quantity:</strong> {item.quantity}</p>
                    <p><strong>Price:</strong> {formatPrice(item.price || 0)}</p>
                    {item.size && item.size !== 'null' && (
                      <p><strong>Size:</strong> {item.size}</p>
                    )}
                    {item.color && item.color !== 'null' && (
                      <p><strong>Color:</strong> {item.color}</p>
                    )}
                    {item.wholesaleDiscount > 0 && (
                      <p><strong>Wholesale Discount:</strong> {item.wholesaleDiscount}%</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            <div className={styles.orderTotal}>
              <h4>Total: {formatPrice(editedOrder.totalAmount || 0)}</h4>
              {editedOrder.commission > 0 && (
                <p className={styles.commission}>Commission: {formatPrice(editedOrder.commission)}</p>
              )}
            </div>
          </div>
          
          <div className={styles.modalSection}>
            <h3>Marketer Information</h3>
            <p><strong>Name:</strong> {editedOrder.marketerName || 'N/A'}</p>
            <p><strong>Email:</strong> {editedOrder.marketerEmail || 'N/A'}</p>
          </div>
          
          <div className={styles.modalSection}>
            <h3>Admin Notes</h3>
            <div className={styles.formGroup}>
              <textarea
                value={editedOrder.admin_note || ''}
                onChange={(e) => handleChange('admin_note', e.target.value)}
                placeholder="Add notes for this order (only visible to admin and marketer)"
                rows={4}
                className={styles.adminNoteTextarea}
              />
            </div>
          </div>
        </div>
        
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
          <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
            {saving ? <><FaSpinner className={styles.spinner} /> Saving...</> : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingField, setSavingField] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc'); // 'desc' for newest first, 'asc' for oldest
  const [sortField, setSortField] = useState('date'); // 'date' or 'status'
  const [orderCounter, setOrderCounter] = useState(0);
  const [marketerFilter, setMarketerFilter] = useState('allOrders'); // default to all orders
  const [marketers, setMarketers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(null);
  
  // Modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Pagination state
  const [lastVisibleOrder, setLastVisibleOrder] = useState(null);
  const [firstVisibleOrder, setFirstVisibleOrder] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationHistory, setPaginationHistory] = useState([]);

  // Function to open the order details modal
  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  // Function to get the main product name from order items
  const getMainProductName = (items = []) => {
    if (!items || items.length === 0) return 'No products';
    const item = items[0];
    let productName = item.name || 'Unnamed product';
    
    // Add size and color information if available
    const sizeInfo = (item.size && item.size !== 'null') ? ` (Size: ${item.size})` : '';
    const colorInfo = (item.color && item.color !== 'null') ? ` (Color: ${item.color})` : '';
    
    return productName + sizeInfo + colorInfo;
  };
  
  // Function to handle saving order changes from the modal
  const handleSaveOrderChanges = async (updatedOrder) => {
    try {
      const orderRef = doc(db, 'orders', updatedOrder.id);
      
      // Update the order status if it has changed
      if (updatedOrder.status !== selectedOrder.status) {
        await updateOrderStatus(updatedOrder.id, updatedOrder.status);
      }
      
      // Create an updates object with properties that need to be updated
      const updates = {
        customerInfo: updatedOrder.customerInfo || {}
      };
      
      // Add admin_note to updates if it has changed or exists
      if (updatedOrder.admin_note !== selectedOrder.admin_note || updatedOrder.admin_note) {
        updates.admin_note = updatedOrder.admin_note || '';
      }
      
      // Update the order in Firestore
      await updateDoc(orderRef, updates);
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === updatedOrder.id ? updatedOrder : order
      ));
      
      return true;
    } catch (err) {
      console.error('Error updating order:', err);
      setError('Failed to update order');
      return false;
    }
  };

  const orderStatuses = ['pending', 'processing', 'shipped', 'delivered', 'cancelled', 'returned'];

  // Fetch marketers for the filter dropdown
  useEffect(() => {
    const fetchMarketers = async () => {
      try {
        const marketersQuery = query(collection(db, 'users'), orderBy('name'));
        const marketersSnapshot = await getDocs(marketersQuery);
        const marketersList = marketersSnapshot.docs
          .filter(doc => doc.data().role === 'marketer')
          .map(doc => ({
            id: doc.id,
            name: doc.data().name,
            email: doc.data().email
          }));
        setMarketers(marketersList);
      } catch (error) {
        console.error('Error fetching marketers:', error);
      }
    };
    
    fetchMarketers();
  }, []);

  // Subscribe to orders with pagination
  useEffect(() => {
    setLoading(true);
    setLastVisibleOrder(null);
    setFirstVisibleOrder(null);
    setOrders([]);
    setHasNextPage(true);
    setHasPrevPage(false);
    setCurrentPage(1);
    setPaginationHistory([]);
    
    // Create filters object for the subscription
    let filters = {
      status: statusFilter !== 'all' ? statusFilter : undefined,
      sortDirection: sortDirection,
      sortField: sortField
    };
    if (marketerFilter === 'allMarketers') {
      filters.marketerId = '__ALL_MARKETERS__'; // special flag
    } else if (marketerFilter === 'shop') {
      filters.marketerId = '__SHOP__'; // special flag
    } else if (marketerFilter !== 'allOrders') {
      filters.marketerId = marketerFilter;
    }
    
    const unsubscribe = subscribeToAdminOrders(
      (result) => {
        setOrders(result.orders);
        setLastVisibleOrder(result.lastVisible);
        setFirstVisibleOrder(result.firstVisible);
        setHasNextPage(result.hasMore);
        setOrderCounter(result.orders.length);
        setLoading(false);
        // Initialize pagination history with first page
        if (result.orders.length > 0) {
          setPaginationHistory([{
            firstVisible: result.firstVisible,
            lastVisible: result.lastVisible,
            page: 1
          }]);
        }
      },
      ordersPerPage,
      null, // start with no lastVisible for the initial load
      filters
    );
    return () => {
      unsubscribe();
    };
  }, [sortDirection, statusFilter, marketerFilter, ordersPerPage]);

  const calculateOrderTotal = (items) => {
    return items.reduce((sum, item) => {
      if (!item) return sum;
      const unitPrice = item.isWholesaleApplied ? 
        (item.price || 0) * (1 - ((item.wholesaleDiscount || 0) / 100)) : 
        (item.price || 0);
      return sum + (unitPrice * (item.quantity || 1));
    }, 0);
  };

  const formatPrice = (price) => {
    return '$' + price.toLocaleString('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      setUpdatingStatus(orderId);
      
      const result = await updateOrderStatus(orderId, newStatus);
      
      if (result.success) {
        setOrders(orders.map(order => 
          order.id === orderId ? { ...order, status: newStatus } : order
        ));
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Failed to update order status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Function removed to avoid duplication - using the enhanced version below

  const truncateText = (text, limit = 50) => {
    if (!text) return 'N/A';
    return text.length > limit ? `${text.substring(0, limit)}...` : text;
  };

  const startEditing = (orderId, field, value) => {
    setEditingField(`${orderId}-${field}`);
    setEditingValue(value || '');
  };

  const handleSave = async (orderId, field, value) => {
    try {
      setSavingField(`${orderId}-${field}`);
      
      // Split field path into parts to handle nested fields
      const fieldParts = field.split('.');
      
      // Create the update object dynamically
      let updateData = {};
      
      if (fieldParts.length === 1) {
        // Direct field update (like 'admin_note')
        updateData[field] = value;
      } else if (fieldParts.length === 2) {
        // Nested field update (like 'customerInfo.notes')
        const [parent, child] = fieldParts;
        
        // If updating customerInfo, use mergeFields to preserve other fields
        if (parent === 'customerInfo') {
          const orderRef = doc(db, 'orders', orderId);
          const orderDoc = await getDoc(orderRef);
          const orderData = orderDoc.data();
          
          if (orderData && orderData.customerInfo) {
            updateData[parent] = {
              ...orderData.customerInfo,
              [child]: value
            };
          } else {
            updateData[parent] = { [child]: value };
          }
        } else {
          updateData[parent] = { [child]: value };
        }
      }
      
      await updateDoc(doc(db, 'orders', orderId), updateData);
      
      // Update the local state with the new value
      setOrders(orders.map(order => {
        if (order.id === orderId) {
          // Create a copy of the order to update
          const updatedOrder = { ...order };
          
          // Update based on field path
          if (fieldParts.length === 1) {
            updatedOrder[field] = value;
          } else if (fieldParts.length === 2) {
            const [parent, child] = fieldParts;
            updatedOrder[parent] = {
              ...updatedOrder[parent],
              [child]: value
            };
          }
          
          return updatedOrder;
        }
        return order;
      }));
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setError(`Failed to update ${field}`);
    } finally {
      setEditingField(null);
      setSavingField(null);
    }
  };

  const renderEditableField = (order, field, value, type = 'text') => {
    const isEditing = editingField === `${order.id}-${field}`;
    const isSaving = savingField === `${order.id}-${field}`;

    if (isEditing) {
      return (
        <div className="editable-field-container">
          {type === 'textarea' ? (
            <textarea
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={() => handleSave(order.id, field, editingValue)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSave(order.id, field, editingValue);
                }
              }}
              className="editable-input textarea"
              autoFocus
            />
          ) : (
            <input
              type={type}
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={() => handleSave(order.id, field, editingValue)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSave(order.id, field, editingValue);
                }
              }}
              className="editable-input"
              autoFocus
            />
          )}
          <div className="edit-actions">
            <button onClick={() => handleSave(order.id, field, editingValue)}>
              <FaCheck />
            </button>
            <button onClick={() => setEditingField(null)}>
              <FaTimes />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="editable-field">
        <span>{value}</span>
        <button
          onClick={() => startEditing(order.id, field, value)}
          className="edit-button"
          disabled={isSaving}
        >
          {isSaving ? (
            <div className="saving-spinner" />
          ) : (
            <FaPencilAlt />
          )}
        </button>
      </div>
    );
  };

  const renderPrice = (price, isDiscounted = false) => {
    const formattedPrice = formatPrice(price);
    return isDiscounted ? (
      <span className="discounted-price">{formattedPrice}</span>
    ) : (
      <span className="regular-price">{formattedPrice}</span>
    );
  };

  const renderOrderItem = (item) => {
    const originalPrice = item?.price || 0;
    const discountedPrice = item?.isWholesaleApplied ? 
      originalPrice * (1 - (item.wholesaleDiscount / 100)) :
      (item?.offerPrice || originalPrice);
    const total = (item?.quantity || 1) * discountedPrice;

    return (
      <div className="order-item">
        <div className="item-name">{item?.name || 'Unknown Product'}</div>
        <div className="item-details">
          {item?.size && item.size !== 'null' && (
            <div className="item-size">
              <strong>Size:</strong> {item.size}
            </div>
          )}
          {item?.color && item.color !== 'null' && (
            <div className="item-color">
              <strong>Color:</strong> {item.color}
            </div>
          )}
          <div className="item-price-details">
            <div className="price-container">
              {(item?.isWholesaleApplied || item?.offerPrice) && (
                <span className="original-price">{formatPrice(originalPrice)}</span>
              )}
              <span className="final-price">{formatPrice(discountedPrice)}</span>
              <span className="quantity">× {item?.quantity || 1}</span>
            </div>
            {(item?.isWholesaleApplied || item?.offerPrice) && (
              <div className="discount-tag">
                {item?.isWholesaleApplied ? 
                  `Wholesale: ${item.wholesaleDiscount}% off` : 
                  'Special Offer'}
              </div>
            )}
            <div className="item-total">
              Total: {formatPrice(total)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAddress = (order) => {
    const address = order.customerInfo?.address || 'N/A';
    const isEditing = editingField === `${order.id}-customer.address`;

    if (isEditing) {
      return (
        <div className="editable-field-container">
          <textarea
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={() => handleSave(order.id, 'customer.address', editingValue)}
            className="editable-input address-textarea"
            autoFocus
          />
          <div className="edit-actions">
            <button onClick={() => handleSave(order.id, 'customer.address', editingValue)}>
              <FaCheck />
            </button>
            <button onClick={() => setEditingField(null)}>
              <FaTimes />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="address-display" data-full-address={address}>
        <span className="truncated-address">{truncateText(address, 50)}</span>
        <button
          onClick={() => startEditing(order.id, 'customer.address', address)}
          className="edit-button"
          title="Edit Address"
        >
          <FaPencilAlt />
        </button>
      </div>
    );
  };

  const renderCustomerNotes = (order) => {
    const notes = order.customerInfo?.notes;
    if (!notes) return null;
    
    return (
      <div className="customer-notes">
        <details>
          <summary>Customer Notes</summary>
          <p>{notes}</p>
        </details>
      </div>
    );
  };

  const renderAdminNotes = (order) => {
    const isEditing = editingField === `${order.id}-admin_note`;
    const notes = order.admin_note || '';
    
    if (isEditing) {
      return (
        <div className={styles.adminNotesEdit}>
          <textarea
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={() => handleSave(order.id, 'admin_note', editingValue)}
            className={styles.adminNotesTextarea}
            autoFocus
            placeholder="Add admin notes"
          />
          <div className={styles.editActions}>
            <button onClick={() => handleSave(order.id, 'admin_note', editingValue)}>
              <FaCheck />
            </button>
            <button onClick={() => setEditingField(null)}>
              <FaTimes />
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className={styles.adminNotes}>
        <div className={styles.adminNotesHeader}>
          <span className={styles.adminNotesTitle}>Admin Notes</span>
          <button
            onClick={() => startEditing(order.id, 'admin_note', notes)}
            className={styles.editButton}
            title="Edit Admin Notes"
          >
            <FaPencilAlt />
          </button>
        </div>
        {notes ? (
          <p>{notes}</p>
        ) : (
          <p className={styles.emptyNote}>No admin notes yet. Click to add.</p>
        )}
      </div>
    );
  };

  const formatOrderNumber = (num) => {
    return `#${String(num).padStart(5, '0')}`;
  };
  
  // Format any order number consistently
  const formatAnyOrderNumber = (order) => {
    if (order.orderNumber) {
      return formatOrderNumber(order.orderNumber);
    } else {
      return 'N/A';
    }
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    
    // Parse the sort value which is in format 'field_direction'
    const [field, direction] = value.split('_');
    
    setSortField(field);
    setSortDirection(direction);
  };

  const handleMarketerFilterChange = (e) => {
    setMarketerFilter(e.target.value);
  };
  
  // Function to handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Enhanced delete order function with confirmation and loading state
  const handleDeleteOrder = async (orderId, e) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      try {
        setIsDeleting(orderId);
        await deleteDoc(doc(db, 'orders', orderId));
        
        // Update local state to remove the deleted order
        setOrders(orders.filter(order => order.id !== orderId));
        
        // Show success message
        alert('Order deleted successfully');
      } catch (err) {
        console.error('Error deleting order:', err);
        setError('Failed to delete order');
      } finally {
        setIsDeleting(null);
      }
    }
  };
  
  // Function to load next page of orders
  const loadNextPage = async () => {
    if (!hasNextPage || isLoadingPage) return;
    
    setIsLoadingPage(true);
    
    try {
      // Create filters object for the subscription
      const filters = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sortDirection: sortDirection
      };
      
      const unsubscribe = subscribeToAdminOrders(
        (result) => {
          
          setOrders(result.orders);
          setFirstVisibleOrder(result.firstVisible);
          setLastVisibleOrder(result.lastVisible);
          setHasNextPage(result.hasMore);
          setHasPrevPage(true);
          setIsLoadingPage(false);
          setCurrentPage(prev => prev + 1);
          
          // Add this page to pagination history
          setPaginationHistory(prev => [...prev, {
            firstVisible: result.firstVisible,
            lastVisible: result.lastVisible,
            page: currentPage + 1
          }]);
          
          unsubscribe(); // Unsubscribe after receiving the data since we're just getting a page
        },
        ordersPerPage,
        lastVisibleOrder,
        filters
      );
    } catch (error) {
      console.error('Error loading next page of orders:', error);
      setError('Failed to load orders');
      setIsLoadingPage(false);
    }
  };
  
  // Function to load previous page of orders
  const loadPrevPage = async () => {
    if (!hasPrevPage || isLoadingPage || currentPage <= 1) return;
    
    setIsLoadingPage(true);
    
    try {
      // Get the previous page info from history
      const prevPageIndex = currentPage - 2; // -2 because we want to go back one page and arrays are 0-indexed
      const prevPageInfo = paginationHistory[prevPageIndex];
      
      if (!prevPageInfo) {
        setIsLoadingPage(false);
        return;
      }
      
      // Create filters object for the subscription
      const filters = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sortDirection: sortDirection
      };
      
      // For previous page, we need to start from the beginning and use limitToLast
      // This is handled in the service function when we pass the firstVisible
      const unsubscribe = subscribeToAdminOrders(
        (result) => {
          
          setOrders(result.orders);
          setFirstVisibleOrder(result.firstVisible);
          setLastVisibleOrder(result.lastVisible);
          setHasNextPage(true); // We know there's a next page since we came from there
          setHasPrevPage(currentPage > 2); // There's a previous page if we're not going to page 1
          setIsLoadingPage(false);
          setCurrentPage(prev => prev - 1);
          
          unsubscribe(); // Unsubscribe after receiving the data
        },
        ordersPerPage,
        null, // Don't use lastVisible for going back
        filters,
        prevPageInfo.firstVisible // Use the firstVisible from the previous page
      );
    } catch (error) {
      console.error('Error loading previous page of orders:', error);
      setError('Failed to load previous page');
      setIsLoadingPage(false);
    }
  };

  if (loading) return <div className={styles.loading}>Loading orders...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  // Filter orders based on status, marketer, and search term
  let filteredOrders = orders.filter(order => {
    const statusMatch = statusFilter === 'all' || order.status === statusFilter;
    let marketerMatch = true;
    if (marketerFilter === 'allMarketers') {
      marketerMatch = !!order.marketerId;
    } else if (marketerFilter === 'shop') {
      marketerMatch = !order.marketerId;
    } else if (marketerFilter !== 'allOrders') {
      marketerMatch = order.marketerId === marketerFilter;
    }
    // Search functionality
    let searchMatch = true;
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      searchMatch = (
        (order.orderNumber && order.orderNumber.toString().includes(searchLower)) ||
        (order.customerName && order.customerName.toLowerCase().includes(searchLower)) ||
        (order.customerPhone && order.customerPhone.toLowerCase().includes(searchLower)) ||
        (order.customerEmail && order.customerEmail.toLowerCase().includes(searchLower)) ||
        (order.customerAddress && order.customerAddress.toLowerCase().includes(searchLower)) ||
        (order.items && order.items.some(item => 
          (item.name && item.name.toLowerCase().includes(searchLower)) ||
          (item.size && item.size.toLowerCase().includes(searchLower)) ||
          (item.color && item.color.toLowerCase().includes(searchLower))
        )) ||
        (order.marketerName && order.marketerName.toLowerCase().includes(searchLower)) ||
        (order.status && order.status.toLowerCase().includes(searchLower)) ||
        (order.admin_note && order.admin_note.toLowerCase().includes(searchLower))
      );
    }
    return statusMatch && marketerMatch && searchMatch;
  });
  
  // If we're doing client-side sorting by status and server-side sorting isn't working
  // This is a fallback in case the server-side sorting has issues
  if (sortField === 'status' && filteredOrders.length > 0) {
    // Clone the array to avoid mutating the original
    filteredOrders = [...filteredOrders].sort((a, b) => {
      // Define a status priority order
      const statusPriority = {
        'pending': 1,
        'processing': 2,
        'shipped': 3,
        'delivered': 4,
        'cancelled': 5,
        'returned': 6
      };
      
      // Get the priority or use a high number for unknown statuses
      const priorityA = statusPriority[a.status] || 999;
      const priorityB = statusPriority[b.status] || 999;
      
      // Sort based on direction
      return sortDirection === 'asc' 
        ? priorityA - priorityB 
        : priorityB - priorityA;
    });
  }

  return (
    <div className={styles.ordersContainer}>
      <div className={styles.ordersHeader}>
        <h2>Orders Management</h2>
        
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search orders by customer, product, status..."
            value={searchTerm}
            onChange={handleSearchChange}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label>Status:</label>
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">All Statuses</option>
              {orderStatuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label>Order Source:</label>
            <select
              value={marketerFilter}
              onChange={handleMarketerFilterChange}
              className={styles.filterSelect}
            >
              <option value="allOrders">All Orders</option>
              <option value="shop">Shop Orders</option>
              <option value="allMarketers">All Marketers</option>
              {marketers.map(marketer => (
                <option key={marketer.id} value={marketer.id}>
                  {marketer.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label>Sort:</label>
            <select
              value={`${sortField}_${sortDirection}`}
              onChange={handleSortChange}
              className={styles.filterSelect}
            >
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="status_asc">Status (Pending → Delivered)</option>
              <option value="status_desc">Status (Delivered → Pending)</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className={styles.resultsInfo}>
        {filteredOrders.length > 0 ? (
          <p>Showing {filteredOrders.length} order(s)</p>
        ) : (
          <p>No orders found matching your criteria</p>
        )}
      </div>
      
      {filteredOrders.length === 0 ? (
        <div className={styles.noOrders}>No orders found matching the current filters.</div>
      ) : (
        <div className={styles.ordersList}>
          {filteredOrders.map((order) => (
            <div key={order.id} className={styles.orderCard} onClick={() => openOrderDetails(order)}>
              <div className={styles.orderHeader}>
                <div className={styles.orderInfo}>
                  <h3>{formatAnyOrderNumber(order)}</h3>
                  <p className={styles.orderDate}>
                    {order.createdAt instanceof Date 
                      ? order.createdAt.toLocaleDateString() 
                      : 'N/A'}
                  </p>
                </div>
                <div className={`${styles.statusBadge} ${styles[order.status || 'pending']}`}>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </div>
              </div>
              
              <div className={styles.productInfo}>
                <p><strong>Product:</strong> {getMainProductName(order.items)}</p>
                {order.items && order.items.length > 1 && 
                  <p className={styles.additionalItems}>+{order.items.length - 1} more items</p>
                }
              </div>
              
              {/* Admin Notes - Inline Editable */}
              <div className={styles.adminNotesSection} onClick={(e) => e.stopPropagation()}>
                {renderAdminNotes(order)}
              </div>
              
              <div className={styles.orderfooter}>
                <div className={styles.marketerInfo}>
                  <strong>Marketer:</strong> {order.marketerName || 'N/A'}
                </div>
                <div className={styles.orderActions}>
                  <button 
                    className={styles.editButton} 
                    onClick={(e) => {
                      e.stopPropagation();
                      openOrderDetails(order);
                    }}
                  >
                    <FaEdit /> Details
                  </button>
                  <button 
                    className={styles.deleteButton} 
                    onClick={(e) => handleDeleteOrder(order.id, e)}
                    disabled={isDeleting === order.id}
                  >
                    {isDeleting === order.id ? (
                      <><FaSpinner className={styles.spinner} /></>
                    ) : (
                      <><FaTrash /> Delete</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className={styles.paginationContainer}>
        <button 
          className={styles.paginationButton}
          onClick={loadPrevPage}
          disabled={!hasPrevPage || isLoadingPage}
        >
          {isLoadingPage && currentPage > 1 ? (
            <><FaSpinner className={styles.spinner} /> Loading...</>
          ) : (
            'Previous Page'
          )}
        </button>
        
        <span className={styles.pageIndicator}>Page {currentPage}</span>
        
        <button 
          className={styles.paginationButton}
          onClick={loadNextPage}
          disabled={!hasNextPage || isLoadingPage}
        >
          {isLoadingPage && hasNextPage ? (
            <><FaSpinner className={styles.spinner} /> Loading...</>
          ) : (
            'Next Page'
          )}
        </button>
      </div>
      
      {/* Order Detail Modal */}
      <OrderDetailModal 
        order={selectedOrder}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveOrderChanges}
        orderStatuses={orderStatuses}
      />
    </div>
  );
}

export default Orders;